import { FormInput, FormSelect, SecondaryButton } from "../../components";
import { IDropLocation } from "../../store/useDropLocations";
import {
  ILocationRateRow,
  ILocationRateRowError,
} from "../../utils/locationRates";
import { storeLabel } from "../../utils/storeLabel";

interface Props {
  rows: ILocationRateRow[];
  onChange: (rows: ILocationRateRow[]) => void;
  locations: IDropLocation[];
  /** Per-row errors from validateLocationRateRows; shown once submitted. */
  errors: (ILocationRateRowError | null)[];
  showErrors: boolean;
  /** The material's default Droppie revenue per kg, pre-filled in new rows. */
  defaultRevenue?: string | number;
  disabled?: boolean;
}

/**
 * Locations with their own consumer payout rate and their own Droppie revenue
 * per kg for this material (e.g. both €0 at one location). An exception
 * always sets both. Only super admins may change these; the backend refuses
 * locationRates from anyone else.
 */
export const LocationRatesEditor = ({
  rows,
  onChange,
  locations,
  errors,
  showErrors,
  defaultRevenue,
  disabled,
}: Props) => {
  const updateRow = (index: number, change: Partial<ILocationRateRow>) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));
  const removeRow = (index: number) =>
    onChange(rows.filter((_, i) => i !== index));
  const addRow = () =>
    onChange([
      ...rows,
      { location: "", rate: "", revenue: String(defaultRevenue ?? "") },
    ]);

  const locationOptions = [
    { value: "", label: "Pick a location", disabled: true },
    ...[...locations]
      .map((location) => ({
        value: String(location?._id),
        // "name (cost_place)", as stores are shown elsewhere: names are not
        // unique.
        label: storeLabel(location) || String(location?._id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  return (
    <div className="mb-4">
      <p className="font-satoshi text-dark text-sm font-medium mb-1">
        Rate and revenue per location
      </p>
      <p className="font-satoshi text-gray-text text-sm mb-3">
        Locations where this material has its own consumer payout rate and its
        own Droppie revenue per kg. All other locations use the rate and
        revenue above.
      </p>
      {rows.length === 0 ? (
        <p className="font-satoshi text-gray-text text-sm mb-3">
          No location exceptions.
        </p>
      ) : null}
      {rows.map((row, index) => {
        const error = showErrors ? errors[index] : null;
        // A location deleted elsewhere is not in the list; keep it visible.
        const options = locationOptions.some((o) => o.value === row.location)
          ? locationOptions
          : [
              ...locationOptions,
              { value: row.location, label: "Unknown location" },
            ];
        return (
          <div key={index} className="mb-2">
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-end sm:gap-x-3">
              {/* Its own line: next to the rate, the revenue and Remove it was
                  squeezed to about 50px and no store name could be read. */}
              <div className="sm:basis-full">
                <FormSelect
                  label={index === 0 ? "Location" : undefined}
                  labelClassName={index === 0 ? "" : "hidden"}
                  value={row.location}
                  options={options}
                  onChange={(e) => updateRow(index, { location: e.target.value })}
                  error={error?.field === "location"}
                  disabled={disabled}
                />
              </div>
              <div className="sm:w-36">
                <FormInput
                  label={index === 0 ? "Rate (€)" : undefined}
                  labelClassName={index === 0 ? "" : "hidden"}
                  value={row.rate}
                  placeholder="e.g. 0"
                  type="text"
                  onChange={(e) => updateRow(index, { rate: e.target.value })}
                  error={error?.field === "rate"}
                  disabled={disabled}
                />
              </div>
              <div className="sm:w-36">
                <FormInput
                  label={index === 0 ? "Revenue (€/kg)" : undefined}
                  labelClassName={index === 0 ? "" : "hidden"}
                  value={row.revenue}
                  placeholder="e.g. 0"
                  type="text"
                  onChange={(e) => updateRow(index, { revenue: e.target.value })}
                  error={error?.field === "revenue"}
                  disabled={disabled}
                />
              </div>
              {!disabled ? (
                <div className="mb-4">
                  <SecondaryButton
                    type="button"
                    label="Remove"
                    onClick={() => removeRow(index)}
                  />
                </div>
              ) : null}
            </div>
            {error ? (
              <p className="font-satoshi text-sm text-red-base -mt-2 mb-2">
                {error.message}
              </p>
            ) : null}
          </div>
        );
      })}
      {!disabled ? (
        <SecondaryButton
          type="button"
          label="Add location exception"
          onClick={addRow}
        />
      ) : null}
    </div>
  );
};
